// client/src/components/agendamentos/AgendamentoListItem.js
import React from 'react';
import { User, Briefcase, MapPin, Package, Truck } from 'lucide-react';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';

const parseDateSafe = (d) => {
    if (!d) return new Date();
    if (typeof d === 'string' || typeof d === 'number') return new Date(d);
    if (d.$date) return new Date(d.$date);
    return new Date(d);
};

const AgendamentoListItem = ({ agendamento, onClick }) => {
    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();

    const getStatusClass = (status) => {
        const statusNormalized = status?.toLowerCase().replace(/\s+/g, '-');
        switch (statusNormalized) {
            case 'confirmado':
            case 'concluido':
                return 'status-green';
            case 'agendado':
                return 'status-blue';
            case 'pendente':
                return 'status-yellow';
            case 'cancelado':
            case 'noshow':
                return 'status-red';
            default:
                return 'status-gray';
        }
    };

    const getBadgeClass = (status) => {
        const statusNormalized = status?.toLowerCase().replace(/\s+/g, '-');
        switch (statusNormalized) {
            case 'confirmado': case 'concluido': return 'badge-green';
            case 'agendado': return 'badge-blue';
            case 'pendente': return 'badge-yellow';
            case 'cancelado': case 'noshow': return 'badge-red';
            default: return 'badge-gray';
        }
    };

    // ✅ 'NoShow' é o valor salvo no banco (ver models/Agendamento.js) — sem
    // rótulo em português aqui, o badge mostraria "NoShow" cru, quebrando o
    // padrão de UI 100% em português do resto da tela.
    const getStatusLabel = (status) => status === 'NoShow' ? 'Não Compareceu' : status;

    const formatTime = (dateString) => {
        if (!dateString) return '';
        try {
            return parseDateSafe(dateString).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } catch (e) { return 'Inválido'; }
    };

    const formatDateShort = (dateString) => {
        if (!dateString) return '';
        try {
            return parseDateSafe(dateString).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        } catch (e) { return ''; }
    };

    // ✅ CORREÇÃO: Caminho correto para o nome do cliente
    const clienteNome = agendamento.participante?.nome || 
                        agendamento.participante?.user?.name || 
                        agendamento.participante?.name || 
                        'Cliente não informado';
    
    // ✅ CORREÇÃO: Nome dos itens (serviços ou recursos) COM QUANTIDADE
    const formatarItensComQuantidade = (itens) => {
        if (!itens || itens.length === 0) return 'Nenhum item';
        
        return itens.map(item => {
            const nome = item.descricao || item.item?.nome || 'Item';
            const quantidade = item.quantidade || 1;
            
            if (quantidade > 1) {
                return `${quantidade}x ${nome}`;
            }
            return nome;
        }).join(' + ');
    };
    
    const itensNomes = formatarItensComQuantidade(agendamento.itens);
    
    // ✅ Identificar tipo do agendamento para ícone
    const isLocacaoExterna = agendamento.tipoReserva?.subcategoria === 'EXTERNO_LOCACAO';
    const isLocacaoInterna = agendamento.tipoReserva?.subcategoria === 'INTERNO_LOCACAO';
    const isServico = agendamento.tipoReserva?.subcategoria?.includes('SERVICOS');
    
    const getTypeIcon = () => {
        if (isLocacaoExterna) return <Truck size={14} className="icon text-orange-500" />;
        if (isLocacaoInterna) return <Package size={14} className="icon text-purple-500" />;
        return <Briefcase size={14} className="icon text-blue-500" />;
    };
    
    const getTypeBadge = () => {
        if (isLocacaoExterna) return 'bg-orange-100 text-orange-700';
        if (isLocacaoInterna) return 'bg-purple-100 text-purple-700';
        return 'bg-blue-100 text-blue-700';
    };
    
    const getTypeLabel = () => {
        if (isLocacaoExterna) return 'Locação Externa';
        if (isLocacaoInterna) return 'Locação Interna';
        return 'Serviço';
    };
        
    const valorTotal = agendamento.financeiro?.total;
    const observacoes = agendamento.observacoes;

    // Controle de Sessão (Servico.controlaSessao) e Controle de Pacotes
    // (PacoteCliente) — dois indicadores independentes, ambos opcionais.
    const itemComSessao = (agendamento.itens || []).find(item => item.numeroSessao);
    const dadosPacote = agendamento.metadataPacote?.utilizado ? agendamento.metadataPacote.dadosPacote : null;
    const sessaoDoPacote = dadosPacote
        ? Math.max(1, (dadosPacote.sessoesTotais || 0) - (dadosPacote.sessoesRestantes || 0))
        : null;

    const isMultiDay = !!agendamento._isMultiDaySegment;

    return (
        <li className={`agenda-list-item ${getStatusClass(agendamento.status)} ${isMultiDay ? 'is-multiday' : ''}`} onClick={onClick}>
            <div className="item-time">
                {isMultiDay ? (
                    <>
                        <span>{formatDateShort(agendamento.dataInicio)} {formatTime(agendamento.dataInicio)}</span>
                        <span className="text-xs text-gray-400 mx-1">→</span>
                        <span>{formatDateShort(agendamento.dataFim)} {formatTime(agendamento.dataFim)}</span>
                    </>
                ) : (
                    <>{formatTime(agendamento.dataInicio)} - {formatTime(agendamento.dataFim)}</>
                )}
            </div>
            
            <div className="item-details">
                <h4 className="item-title">
                    <User size={14} className="icon" />
                    {clienteNome}
                </h4>
                <p className="item-subtext">
                    {getTypeIcon()}
                    {itensNomes}
                    <span className={`inline-flex items-center ml-2 px-1.5 py-0.5 rounded-full text-xs ${getTypeBadge()}`}>
                        {getTypeLabel()}
                    </span>
                </p>
                {(itemComSessao || dadosPacote || agendamento.sinal?.exigido) && (
                    <p className="item-subtext text-xs">
                        {itemComSessao && (
                            <span className="inline-flex items-center mr-2 px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold" title="Controle de Sessão">
                                Sessão {itemComSessao.numeroSessao}
                            </span>
                        )}
                        {dadosPacote && (
                            <span className="inline-flex items-center mr-2 px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 font-semibold" title={`Controle de Pacote — ${dadosPacote.nome || 'Pacote'}`}>
                                Pacote {sessaoDoPacote}/{dadosPacote.sessoesTotais || 0}
                            </span>
                        )}
                        {agendamento.sinal?.exigido && (
                            agendamento.sinal.status === 'Pago' ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 font-semibold" title="Sinal pago">
                                    Sinal pago
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold" title={`Sinal pendente — R$ ${Number(agendamento.sinal.valor || 0).toFixed(2)}`}>
                                    Sinal pendente
                                </span>
                            )
                        )}
                    </p>
                )}
                {observacoes && (
                    <p className="item-subtext text-gray-400 text-xs truncate max-w-md">
                        Obs: {observacoes}
                    </p>
                )}
            </div>

            <div className="item-meta">
                {typeof valorTotal === 'number' && valorTotal > 0 && (
                    <div className="item-price">{formatCurrency(valorTotal, currency, locale)}</div>
                )}
                <div className="item-status">
                    <span className={`badge ${getBadgeClass(agendamento.status)}`}>{getStatusLabel(agendamento.status)}</span>
                </div>
            </div>
        </li>
    );
};

export default AgendamentoListItem;